import React from 'react'
import OpenSeadragon from 'openseadragon'

type OpenSeadragonViewerProps = Pick<React.HTMLAttributes<HTMLDivElement>, 'className' | 'style'> & {
  options: OpenSeadragon.Options
  initialBounds?: { x1: number; y1: number; x2: number; y2: number }
  onLoad?: () => void
}

export class OpenSeadragonViewerImpl extends React.Component<OpenSeadragonViewerProps> {
  private viewer!: OpenSeadragon.Viewer
  private containerRef = React.createRef<HTMLDivElement>()

  override componentDidMount() {
    if (!this.containerRef.current) return

    this.viewer = OpenSeadragon({
      element: this.containerRef.current,
      showNavigator: true,
      prefixUrl: 'https://openseadragon.github.io/openseadragon/images/',
      showNavigationControl: true,
      showRotationControl: true,
      showZoomControl: true,
      showHomeControl: true,
      showFullPageControl: true,
      zoomPerScroll: 1.4,
      springStiffness: 8,
      defaultZoomLevel: 0,
      maxZoomPixelRatio: 3,
      visibilityRatio: 1,
      constrainDuringPan: true,
      animationTime: 0.5,
      blendTime: 0.1,
      autoHideControls: false,
      gestureSettingsMouse: {
        clickToZoom: true,
        dblClickToZoom: true,
        pinchToZoom: true,
        scrollToZoom: true,
      },
      ...this.props.options,
    })

    this.viewer.addHandler('open', () => {
      this.fitToBounds()
    })

    if (import.meta.env.DEV) {
      // Debug trail: record every viewport move after our fit, so a crop that
      // ends up wrong can be attributed (our fit vs something moving it later).
      const stamp = (tag: string) => {
        const el = this.containerRef.current
        if (!el || !this.viewer?.viewport) return
        const vb = this.viewer.viewport.getBounds(true)
        el.setAttribute('data-vb-last', JSON.stringify({ tag, x: vb.x, y: vb.y, w: vb.width, h: vb.height }))
        el.setAttribute('data-vb-count', String(Number(el.getAttribute('data-vb-count') || 0) + 1))
      }
      this.viewer.addHandler('animation-finish', () => stamp('animation-finish'))
      this.viewer.addHandler('resize', () => stamp('resize'))
    }

    if (this.props.onLoad) {
      this.viewer.addHandler('tile-loaded', () => {
        if (this.props.onLoad) {
          this.props.onLoad()
          this.viewer.removeAllHandlers('tile-loaded')
        }
      })
    }
  }

  // Re-fit when the bounds prop changes: virtualized lists reuse a mounted
  // viewer for a different item, so mount-time-only fitting leaves the old
  // item's crop on screen (rail thumbnails drifting out of sync after an
  // accept/reject shifts the list).
  override componentDidUpdate(prevProps: OpenSeadragonViewerProps) {
    const prev = prevProps.initialBounds
    const next = this.props.initialBounds
    if (!next || !this.viewer?.isOpen()) return
    if (prev && prev.x1 === next.x1 && prev.y1 === next.y1 && prev.x2 === next.x2 && prev.y2 === next.y2) {
      return
    }
    this.fitToBounds()
  }

  private fitToBounds() {
    if (!this.props.initialBounds || !this.viewer) return
    const tiledImage = this.viewer.world.getItemAt(0)
    if (!tiledImage) return
    // OSD viewport coordinates are x ∈ [0,1], y ∈ [0, height/width] regardless
    // of orientation — normalized y always scales by H/W, x never scales.
    // (The old code scaled x by W/H for portrait images, which put the fit a
    // few frame-heights off target — invisible on large bounds, catastrophic
    // on tiny suggestion boxes.)
    const { x1, x2, y1, y2 } = this.props.initialBounds
    const dims = tiledImage.source.dimensions
    const aspect = dims.y / dims.x
    this.viewer.viewport.fitBounds(
      new OpenSeadragon.Rect(x1, y1 * aspect, x2 - x1, (y2 - y1) * aspect),
      true,
    )
    if (import.meta.env.DEV && this.containerRef.current) {
      const vb = this.viewer.viewport.getBounds(true)
      this.containerRef.current.setAttribute('data-fit-requested', JSON.stringify(this.props.initialBounds))
      this.containerRef.current.setAttribute(
        'data-fit-actual',
        JSON.stringify({ x: vb.x, y: vb.y, w: vb.width, h: vb.height }),
      )
    }
  }

  override componentWillUnmount() {
    if (this.viewer) {
      if (this.viewer.isOpen()) {
        this.viewer.close()
      }
      this.viewer.destroy()
      this.viewer = undefined as any
    }
  }

  override render() {
    return <div ref={this.containerRef} className={this.props.className} style={this.props.style} />
  }
}

export default OpenSeadragonViewerImpl
